'use client';

import { Flex, Link, Markdown, Typography } from '@coolblue-development/freddy';
import selectContentTrigger from '@coolblue-development/ga4-events/triggers/PromoBox/selectContentTrigger';
import showPromoboxTrigger from '@coolblue-development/ga4-events/triggers/PromoBox/showPromoboxTrigger';
import viewPromoboxTrigger from '@coolblue-development/ga4-events/triggers/PromoBox/viewPromoboxTrigger';
import { Edit } from '@coolblue-development/icons';
import {
  TrackImmediate,
  TrackWhenVisible,
  useAnalytics
} from '@coolblue-development/next-analytics/client';
import { Image } from '@coolblue-development/next-image/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import type { FormattedCollectionName, PromoBox } from '@features/product/typings';
import { getProductPromoboxManagemodelCMSUrl } from '@utils/manuelaUtils/getProductPromoboxManagemodelCMSUrl';
import { useCallback, useEffect, useRef } from 'react';

type Props = {
  data: PromoBox;
  index: number;
  totalIndices: number;
};

export default function PromoBox({ data: promoBoxData, index, totalIndices }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { isInternalUser } = useRequestContext();
  const { trackEvent } = useAnalytics();

  const getCollectionName = (name: string) => {
    return ({
      Content: 'content',
      Products: 'product',
      Reviews: 'review',
      SecondChance: 'second_chance_product'
    }[name] || 'content') as FormattedCollectionName;
  };

  const onLinkClick = useCallback(
    (event: MouseEvent) => {
      if (event.target) {
        trackEvent(
          selectContentTrigger({
            content: (event.target as HTMLAnchorElement).innerHTML
          })
        );
      }
    },
    [trackEvent]
  );

  useEffect(() => {
    if (contentRef?.current) {
      const links = contentRef.current.getElementsByTagName('a');

      for (const link of links) {
        link.addEventListener('click', onLinkClick);
      }
    }
  }, [contentRef, onLinkClick]);

  if (!promoBoxData) return;

  return (
    <>
      <TrackImmediate trigger={showPromoboxTrigger()} />
      <TrackWhenVisible
        trigger={viewPromoboxTrigger({
          content: promoBoxData.textMarkdown,
          index,
          total_indices: totalIndices
        })}
      >
        <Flex gap={400}>
          <Flex gap={200} direction='column' alignItems='flex-start'>
            <Typography variant='h2'>
              {promoBoxData.title}

              {isInternalUser ? (
                <Link
                  ml={100}
                  aria-label='Edit in Flirt'
                  href={getProductPromoboxManagemodelCMSUrl(promoBoxData.id)}
                >
                  <Edit size='small' />
                </Link>
              ) : (
                ''
              )}
            </Typography>
            <Markdown ref={contentRef}>{promoBoxData.textMarkdown}</Markdown>
          </Flex>
          {promoBoxData.image && (
            <Image
              imageId={promoBoxData.image?.id}
              fileCollection={getCollectionName(promoBoxData.image?.collectionName)}
              width={80}
              height={80}
              alt={promoBoxData.title}
              loading='lazy'
              objectFit='contain'
            />
          )}
        </Flex>
      </TrackWhenVisible>
    </>
  );
}
